import { useMemo } from 'react';

import {
  Grouping,
  TokenUsageWithBreakdown,
} from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/h2ogpte/v1/h2ogpte_pb';
import { Granularity } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';
import { granularityOptions } from '../constants/granularity';
import { TokenUsageType } from '../constants/tokenUsageType';
import { EnrichedWorkspace, FormattedTokenUsageDataResult, User } from '../types';
import { formatTokenUsageDataForChart } from '../utils/tokenDataTransformers';

/**
 * Custom hook to format raw token usage data for chart visualization
 *
 * Transforms the raw API token usage data into a format suitable for the chart component,
 * enriching group IDs with human-readable names.
 *
 * @param usageData Raw token usage data from the API
 * @param granularity The granularity for date formatting
 * @param groupBy The H2OGPTe grouping type
 * @param workspacesMap Map of workspace UUIDs to workspace objects
 * @param usersMap Map of user UUIDs to user objects
 * @param tokenUsageType Which token type to display (INPUT, OUTPUT, TOTAL)
 * @returns Object containing formatted data points and unique groups
 */
export function useFormattedTokenUsageData(
  usageData: TokenUsageWithBreakdown[] | undefined,
  granularity: Granularity,
  groupBy: Grouping,
  workspacesMap: Map<string, EnrichedWorkspace>,
  usersMap: Map<string, User>,
  tokenUsageType: TokenUsageType = TokenUsageType.TOTAL
): FormattedTokenUsageDataResult {
  const formattedResult = useMemo(() => {
    if (!usageData || usageData.length === 0) {
      return { formattedData: [], groups: [] };
    }

    const currentGranularity = granularityOptions.find((option) => option.key === granularity);
    if (!currentGranularity) {
      console.error('[useFormattedTokenUsageData] Invalid granularity:', granularity);
      return { formattedData: [], groups: [] };
    }

    // Wait for maps to be populated before formatting to prevent showing all "Unknown" values
    // This check is only relevant for WORKSPACE and USER groupings
    if (
      (groupBy === Grouping.WORKSPACE || groupBy === Grouping.USER) &&
      (workspacesMap.size === 0 || usersMap.size === 0)
    ) {
      console.warn('[useFormattedTokenUsageData] Maps not yet populated, waiting for data...');
      return { formattedData: [], groups: [] };
    }

    const result = formatTokenUsageDataForChart(
      usageData,
      groupBy,
      workspacesMap,
      usersMap,
      currentGranularity.formatDate,
      tokenUsageType
    );

    return result;
  }, [usageData, granularity, groupBy, workspacesMap, usersMap, tokenUsageType]);

  return {
    formattedUsageData: formattedResult.formattedData,
    groups: formattedResult.groups,
  };
}
